import React from 'react'
import { StyleSheet, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text } from '@components'
import { useLanguage } from '@context'
import { Theme } from '@theme'
import { EventAvatar } from './EventAvatar'

interface AttendeeTeaserProps {
  attendees: { firstName: string; avatarUrl: string | null; isSelf?: boolean }[]
  total: number
  /** Show at most this many faces; the rest becomes "+ N autres". */
  max?: number
}

/** Faces + first names only: what everyone sees before the event is over. */
export function AttendeeTeaser({ attendees, total, max = 24 }: AttendeeTeaserProps) {
  const theme = useTheme()
  const { t } = useLanguage()
  const styles = getStyles(theme)
  const shown = attendees.slice(0, max)
  const rest = Math.max(total - shown.length, 0)

  if (total === 0) {
    return (
      <Text type="body" color={theme.colors.greyBlue}>
        {t('events.public.nobodyYet')}
      </Text>
    )
  }

  return (
    <View style={styles.grid}>
      {shown.map((person, index) => (
        <View key={`${person.firstName}-${index}`} style={styles.cell}>
          <EventAvatar firstName={person.firstName} avatarUrl={person.avatarUrl} size={60} />
          <Text type="bodySmall" center numberOfLines={1} style={styles.name}>
            {person.isSelf ? t('events.public.you') : person.firstName}
          </Text>
        </View>
      ))}
      {rest > 0 ? (
        <View style={styles.cell}>
          <View style={styles.more}>
            <Text type="bodyBold" color={theme.colors.primary}>
              +{rest}
            </Text>
          </View>
          <Text type="bodySmall" center color={theme.colors.greyBlue} style={styles.name}>
            {t('events.public.moreAttendees', { count: rest })}
          </Text>
        </View>
      ) : null}
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    grid: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.lg },
    cell: { width: 76, alignItems: 'center' },
    name: { marginTop: theme.spacing.xs, width: '100%' },
    more: {
      width: 60,
      height: 60,
      borderRadius: 30,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.sidebar,
      borderWidth: 1,
      borderColor: theme.colors.grey,
    },
  })
